import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { Table } from '@org/ui';
import { trpc } from '@org/clients/school';
import {
  GenericModal,
  Button,
  ListToolbar,
  SearchInput,
  SegmentTabs,
  FilterPopover,
  ActiveFilterChips,
  RowActionsMenu,
  StatusChip,
  useListFilters,
  FILTER_ALL,
  type FilterDef,
} from '@org/ui';
import { StudentMultiStepForm } from './student-multi-step-form';
import { StudentExportMenu } from './student-export-menu';
import { JourneysDialog } from '../journey/journeys-dialog';
import { JourneyAccessDialog } from '../journey/journey-access-dialog';
import { unwrapTrpcData } from '@org/ui';
import { useInviteStudent } from '../../hooks/use-invite-student';
import { toast } from 'sonner';
import { Plus, Pencil, Map, KeyRound, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePendingRequests } from '../product/pending-requests-panel';
import {
  buildStudentFilterDefs,
  STUDENT_SITUATIONS,
  type StudentSituation,
  canInvite,
  countBySegment,
  matchesSegment,
  situationOf,
  situationTone,
  type StudentSegment,
} from './student-list-helpers';

type StudentListProps = {
  onCreateStudent?: () => void;
  onEditStudent?: (student: any) => void;
};

export const StudentList: React.FC<StudentListProps> = ({ onCreateStudent, onEditStudent }) => {
  const utils = trpc.useUtils();
  const { data } = trpc.users.listStudentsWithDetails.useQuery();
  const { data: levels } = trpc.levels.list.useQuery();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);
  const [filterValue, setFilterValue] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [pendingInviteId, setPendingInviteId] = React.useState<string | null>(null);
  const [segment, setSegment] = React.useState<StudentSegment>('all');
  const { rows: pendingRequests } = usePendingRequests();
  const pendingAccessUserIds = React.useMemo(
    () => new Set(pendingRequests.map((r: any) => r.userId as string)),
    [pendingRequests],
  );
  const [journeyDialogFor, setJourneyDialogFor] = React.useState<{
    userId: string;
    label: string;
  } | null>(null);
  const [accessDialogFor, setAccessDialogFor] = React.useState<{
    userId: string;
    label: string;
  } | null>(null);
  const inviteStudent = useInviteStudent();
  const { t } = useTranslation('admin');

  const students = React.useMemo(() => data ?? [], [data]);

  const levelsData = React.useMemo(() => unwrapTrpcData(levels) ?? [], [levels]);

  // Levels exactly as the API returns them (no alphabetical sort).
  const levelRows = React.useMemo(
    () =>
      levelsData
        .filter((level) => level.levelId && level.name)
        .map((level) => ({ levelId: String(level.levelId), name: String(level.name) })),
    [levelsData],
  );

  // Everything except the segment lives in the Filters panel, sections in
  // the old toolbar's order: País, Nível, Tipo, Situação, Só ativas. Options
  // and their counts come from the loaded students, so only values that
  // exist are offered.
  const filterDefs = React.useMemo<FilterDef[]>(
    () =>
      buildStudentFilterDefs(
        {
          country: t('students.filters.country'),
          allCountries: t('students.filters.allCountries'),
          level: t('students.filters.level'),
          allLevels: t('students.filters.allLevels'),
          type: t('students.filters.type'),
          allTypes: t('students.filters.allTypes'),
          situation: t('students.situation.column'),
          allSituations: t('students.filters.allSituations'),
          enabledOnly: t('students.filters.enabledOnly'),
          typeNames: {
            group: t('students.form.type.group'),
            private: t('students.form.type.private'),
            'self-learning': t('students.form.type.selfLearning'),
            community: t('students.form.type.community'),
            b2b: t('students.form.type.b2b'),
          },
          situationNames: Object.fromEntries(
            STUDENT_SITUATIONS.map((key) => [key, t(`students.situation.${key}`)]),
          ) as Record<StudentSituation, string>,
        },
        students as any,
        levelRows,
      ),
    [t, students, levelRows],
  );
  const filters = useListFilters(filterDefs, { enabled: true });
  const showEnabledOnly = filters.values.enabled === true;
  const countryFilter = (filters.values.country as string | undefined) ?? FILTER_ALL;
  const levelFilter = (filters.values.level as string | undefined) ?? FILTER_ALL;
  const typeFilter = (filters.values.type as string | undefined) ?? FILTER_ALL;
  const situationFilter = (filters.values.situation as string | undefined) ?? FILTER_ALL;

  const segmentCounts = React.useMemo(() => {
    const scoped = showEnabledOnly ? students.filter((s: any) => s?.enabled) : students;
    return countBySegment(scoped as any, pendingAccessUserIds);
  }, [students, showEnabledOnly, pendingAccessUserIds]);

  const segmentItems = React.useMemo(
    () => [
      { value: 'all', label: t('students.segments.all'), count: segmentCounts.all },
      { value: 'group', label: t('students.segments.group'), count: segmentCounts.group },
      { value: 'private', label: t('students.segments.private'), count: segmentCounts.private },
      { value: 'self-learning', label: t('students.segments.selfLearning'), count: segmentCounts['self-learning'] },
      { value: 'b2b', label: t('students.segments.b2b'), count: segmentCounts.b2b },
      { value: 'pending', label: t('students.segments.pending'), count: segmentCounts.pending },
    ],
    [t, segmentCounts],
  );

  const filteredStudents = React.useMemo(() => {
    let result = students;

    if (showEnabledOnly) {
      result = result.filter((student: any) => student?.enabled);
    }

    result = result.filter((student: any) => matchesSegment(student, segment, pendingAccessUserIds));

    if (countryFilter !== FILTER_ALL) {
      result = result.filter((student: any) => student.countryName === countryFilter);
    }

    if (levelFilter !== FILTER_ALL) {
      result = result.filter((student: any) => student.levelId === levelFilter);
    }

    if (typeFilter !== FILTER_ALL) {
      result = result.filter((student: any) => student.type === typeFilter);
    }

    if (situationFilter !== FILTER_ALL) {
      result = result.filter((student: any) => situationOf(student) === situationFilter);
    }

    return result;
  }, [students, showEnabledOnly, segment, pendingAccessUserIds, countryFilter, levelFilter, typeFilter, situationFilter]);

  const orderedStudents = React.useMemo(() => {
    return [...filteredStudents].sort((studentA: any, studentB: any) => {
      const buildSortKey = (student: any) => {
        const fullName = `${student?.firstName ?? ''} ${student?.lastName ?? ''}`.trim();
        return (fullName || student?.email || '').toLocaleLowerCase();
      };

      return buildSortKey(studentA).localeCompare(buildSortKey(studentB));
    });
  }, [filteredStudents]);

  // What the table is showing right now (filters + search box), so the export
  // matches the screen. Mirrors the Table's `includesString` global filter
  // over its two text columns: full name and level.
  const exportStudentIds = React.useMemo(() => {
    const needle = filterValue.trim().toLocaleLowerCase();
    return orderedStudents
      .filter((student: any) => {
        if (!needle) return true;
        const levelName = levelsData?.find((level) => level.levelId === student.levelId)?.name ?? '';
        const haystack = `${student.firstName ?? ''} ${student.lastName ?? ''} ${levelName}`.toLocaleLowerCase();
        return haystack.includes(needle);
      })
      .map((student: any) => student.userId as string);
  }, [orderedStudents, filterValue, levelsData]);

  // Function to open form for a new user
  const openNewUserForm = () => {
    if (onCreateStudent) {
      onCreateStudent();
      return;
    }

    setSelectedUser(null);
    setIsCreating(true);
    setModalOpen(true);
  };

  // Handle filter changes
  const handleFilterChange = (value: string) => {
    setFilterValue(value);
  };

  // Existing selectUser function...
  const selectUser = (row: any) => {
    if (onEditStudent) {
      onEditStudent(row.original);
      return;
    }

    setIsCreating(false);
    setModalOpen(true);
    setSelectedUser(row.original);
  };

  const onUserFormSubmit = () => {
    setModalOpen(false);
  }

  const columnHelper = createColumnHelper();

  const handleInvite = async (student: any) => {
    if (!student?.userId) {
      return;
    }

    setPendingInviteId(student.userId);

    try {
      await inviteStudent.mutateAsync({ studentId: student.userId });
      toast.success(
        t('students.list.toasts.inviteSuccess', {
          email: student.email ?? t('students.list.labels.studentFallback'),
        }),
      );
      await utils.users.listStudentsWithDetails.invalidate();
    } catch (error) {
      const fallbackMessage = t('common.toasts.actionError', { action: 'send', entity: 'invite' });
      const message = error instanceof Error && error.message ? error.message : fallbackMessage;
      toast.error(message);
    } finally {
      setPendingInviteId(null);
    }
  };

  const columns = [
    columnHelper.display({
      id: 'photo',
      header: t('students.list.table.columns.photo'),
      cell: (info) => {
        const profileImageUrl = (info.row.original as any).profileImageUrl;
        return (
          <div>
            <img
              className="max-h-20 w-20 rounded-full object-cover"
              src={profileImageUrl || "https://pi.tedcdn.com/r/pe.tedcdn.com/images/ted/1ec14eb706a5af446bd2576f9c7ba501884a1f98_254x191.jpg?w=255"}
              alt={t('students.list.table.photoAlt')}
            />
          </div>
        );
      },
    }),
    columnHelper.accessor(
      (row: any) => {
        return `${row.firstName} ${row.lastName}`;
      },
      {
        id: 'firstName',
        header: t('students.list.table.columns.fullName'),
        cell: (info) => {
          const student = info.row.original as any;
          return (
            <div className="flex flex-col">
              <span className="font-medium">{info.getValue() as string}</span>
              {student.email ? <span className="text-xs text-muted-foreground">{student.email}</span> : null}
            </div>
          );
        },
      }
    ),
    columnHelper.accessor(
      (row: any) => {
        return  levelsData?.find((level) => level.levelId === row.levelId)?.name;
      },
      {
        id: 'Level',
        header: t('students.list.table.columns.level'),
      }
    ),
    columnHelper.display({
      id: 'situation',
      header: t('students.situation.column'),
      cell: (info) => {
        const situation = situationOf(info.row.original as any);
        return <StatusChip tone={situationTone[situation]}>{t(`students.situation.${situation}`)}</StatusChip>;
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => {
        const student = info.row.original as any;
        const label =
          `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim() ||
          (student.email ?? t('students.list.labels.studentFallback'));
        const inviting = inviteStudent.isPending && pendingInviteId === student.userId;
        return (
          <div className="flex justify-end">
            <RowActionsMenu
              label={t('students.list.rowActions.label', { name: label })}
              items={[
                { label: t('common.actions.edit'), icon: <Pencil className="h-4 w-4" />, onSelect: () => selectUser(info.row) },
                {
                  label: t('students.list.actions.journeys'),
                  icon: <Map className="h-4 w-4" />,
                  onSelect: () => setJourneyDialogFor({ userId: student.userId, label }),
                },
                {
                  label: t('students.list.actions.access'),
                  icon: <KeyRound className="h-4 w-4" />,
                  onSelect: () => setAccessDialogFor({ userId: student.userId, label }),
                },
                {
                  label: inviting ? t('common.status.inviting') : t('students.list.actions.invite'),
                  icon: <Send className="h-4 w-4" />,
                  onSelect: () => handleInvite(student),
                  disabled: inviting,
                  hidden: !canInvite(student),
                  separatorBefore: true,
                },
              ]}
            />
          </div>
        );
      },
    }),
  ];

  return (
    <div>
      <div className="mx-6 mt-4">
        <ListToolbar
          tabs={
            <SegmentTabs
              value={segment}
              onValueChange={(v) => setSegment(v as StudentSegment)}
              items={segmentItems}
              aria-label={t('students.filters.type')}
            />
          }
          search={<SearchInput onChange={handleFilterChange} placeholder={t('common.placeholders.search')} clearLabel={t('common.actions.clear')} />}
          actions={
            <FilterPopover
              filters={filterDefs}
              values={filters.values}
              onChange={filters.setValue}
              onClear={filters.clear}
              label={t('students.filters.label')}
              clearLabel={t('common.filters.clear')}
              allLabel={t('common.filters.all')}
              searchPlaceholder={t('common.placeholders.search')}
              selectedLabel={t('common.filters.selected')}
            />
          }
          primaryAction={
            <>
              <StudentExportMenu visibleStudentIds={exportStudentIds} />
              <Button onClick={openNewUserForm} variant="outline">
                <Plus className="h-4 w-4" />
                <span className="sr-only sm:not-sr-only">{t('common.actions.add')}</span>
              </Button>
            </>
          }
          chips={
            <ActiveFilterChips
              chips={filters.chips}
              removeLabel={(l) => t('common.filters.remove', { label: l })}
            />
          }
        />
      </div>
      <div><Table
          tableData={orderedStudents as any}
          columns={columns}
          getRowId={(row: any) => row.userId}
          globalFilter={filterValue}
          onRowClick={(row: any) => selectUser({ original: row })}
        /></div>
      {!onCreateStudent && !onEditStudent && modalOpen ? (
          <div><GenericModal
          show={modalOpen}
          onClose={() => setModalOpen(false)}
          title={isCreating ? t('students.list.modal.createTitle') : t('students.list.modal.editTitle')}
          body={
            <div>
              <StudentMultiStepForm
                defaultValues={selectedUser || {}}
                onSubmit={onUserFormSubmit}
                onClose={() => setModalOpen(false)}
                isEditing={!isCreating}
              />
            </div>
          }
          footer={
            <>
            </>
          }
          size="xl"
          centered
        />
        </div>
      ) : null}
      {journeyDialogFor && (
        <JourneysDialog
          entityType="user"
          entityId={journeyDialogFor.userId}
          entityLabel={journeyDialogFor.label}
          open={!!journeyDialogFor}
          onOpenChange={(o) => {
            if (!o) setJourneyDialogFor(null);
          }}
        />
      )}
      {accessDialogFor && (
        <JourneyAccessDialog
          userId={accessDialogFor.userId}
          userLabel={accessDialogFor.label}
          open={!!accessDialogFor}
          onOpenChange={(o) => {
            if (!o) setAccessDialogFor(null);
          }}
        />
      )}
    </div>
  )
}
